import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useUpdateContract } from "@/hooks/api/useContracts";
import type { components } from "@/types/api";

type Contract = components["schemas"]["ContractResponse"];

type Basis = "percentage" | "fixed";

// Percentage is stored as a rate (0.08); shown to the user as whole percent (8).
function toDisplay(contract: Contract): { type: Basis; value: string } {
  const type: Basis = contract.commission_type === "fixed" ? "fixed" : "percentage";
  const value =
    type === "fixed"
      ? String(contract.commission_value ?? 0)
      : String(Math.round((contract.commission_value ?? 0) * 10000) / 100);
  return { type, value };
}

/** The agency's recurring management commission — fixed € or % of each invoice.
 *  Determines the split into the owner's share vs the agency's revenue. */
export function CommissionCard({ contract }: { contract: Contract }) {
  const update = useUpdateContract(contract.id);
  const initial = toDisplay(contract);
  const [editing, setEditing] = useState(false);
  const [type, setType] = useState<Basis>(initial.type);
  const [value, setValue] = useState(initial.value);

  const eur = (n: number) =>
    new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n);

  // Validation: a finite number, percent capped at 100, neither negative.
  const num = Number(value);
  const invalid =
    value.trim() === "" ||
    !Number.isFinite(num) ||
    num < 0 ||
    (type === "percentage" && num > 100);
  const dirty = type !== initial.type || value.trim() !== initial.value;

  // Live echo of what the commission drives: the per-invoice split with the owner.
  const rent = contract.rental_price ?? 0;
  const previewCut = type === "percentage" ? rent * ((num || 0) / 100) : num || 0;
  const agencyCut = Math.min(Math.max(previewCut, 0), rent);
  const ownerShare = Math.max(rent - agencyCut, 0);

  const reset = () => {
    setType(initial.type);
    setValue(initial.value);
    setEditing(false);
  };

  const save = () => {
    if (invalid) return;
    update.mutate(
      {
        commission_type: type,
        commission_value: type === "percentage" ? num / 100 : num,
      } as Record<string, unknown>,
      {
        onSuccess: () => {
          toast.success("Commission updated");
          setEditing(false);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const currentLabel =
    initial.type === "percentage"
      ? `${initial.value}% of rent`
      : `${eur(Number(initial.value) || 0)} fixed`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Management commission</CardTitle>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          The agency's cut of what it collects for the landlord. The rest is owed to the
          owner (see their payouts). VAT {((contract.commission_vat_rate ?? 0) * 100).toFixed(0)}%
          applies.
        </p>

        {editing ? (
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="comm-type">Basis</Label>
              <select
                id="comm-type"
                value={type}
                onChange={(e) => setType(e.target.value as Basis)}
                className="h-9 rounded-md border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="percentage">Percentage of rent</option>
                <option value="fixed">Fixed amount</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="comm-value">
                {type === "percentage" ? "Percent (%)" : "Amount (€)"}
              </Label>
              <Input
                id="comm-value"
                type="number"
                min="0"
                max={type === "percentage" ? "100" : undefined}
                step={type === "percentage" ? "0.1" : "1"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-invalid={invalid}
                className="h-9 w-32"
              />
            </div>
            <Button size="sm" disabled={update.isPending || invalid || !dirty} onClick={save}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="ghost" disabled={update.isPending} onClick={reset}>
              Cancel
            </Button>
          </div>
        ) : (
          <p className="text-sm">
            <span className="text-muted-foreground">Current: </span>
            <span className="font-medium">{currentLabel}</span>
          </p>
        )}

        {invalid && editing && (
          <p className="text-xs text-destructive">
            {type === "percentage"
              ? "Enter a percentage between 0 and 100."
              : "Enter an amount of 0 or more."}
          </p>
        )}

        {rent > 0 && !invalid && (
          <p className="text-sm text-muted-foreground">
            On the current {eur(rent)} rent: ≈{" "}
            <span className="font-medium text-foreground">{eur(agencyCut)}</span> to the
            agency,{" "}
            <span className="font-medium text-foreground">{eur(ownerShare)}</span> to the
            owner per rent invoice (excl. VAT).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
